import React from 'react';

import { Footer } from '../components/footer';
import { Camp67Header } from '../components/camp67-header/camp67-header';

import { TentPageConfig } from './tent';
import { Tent, tents } from '../constants/tents';
import { TentTypeV1 } from '@camp67/model';
import { displayPrice } from '../state/prices';
import { getPrice } from '../state/get-tent-price';
import { useMessages } from '../i18n/useMessages';
import {
  AccommodationContent,
  CAMP67_STANDARD_AMENITIES,
  TWIN_ROOM_HIGHLIGHTS,
  FAMILY_ROOM_HIGHLIGHTS,
  DORM_HIGHLIGHTS,
  PRIVATE_GROUP_HIGHLIGHTS,
} from '../components/accommodation-content';

const getHighlightsForTentType = (tentType: TentTypeV1): string[] => {
  switch (tentType) {
    case TentTypeV1.TWIN_ROOM:
      return TWIN_ROOM_HIGHLIGHTS;
    case TentTypeV1.FAMILY_ROOM:
      return FAMILY_ROOM_HIGHLIGHTS;
    case TentTypeV1.DORM_SINGLE:
    case TentTypeV1.DORM_BUNK:
      return DORM_HIGHLIGHTS;
    case TentTypeV1.PRIVATE_GROUP_COTTAGE:
      return PRIVATE_GROUP_HIGHLIGHTS;
    default:
      return TWIN_ROOM_HIGHLIGHTS;
  }
};

export const TentPage: React.FC<TentPageConfig> = ({ tentType, summary }) => {
  const tent = tents[tentType];

  const getMessage = useMessages();
  const formattedTitle = getMessage(tent.title);

  return (
    <>
      {/* Hero Section */}
      <section className="relative flex flex-col w-dvw h-[55dvh] md:h-[65dvh] max-h-[700px]">
        <Camp67Header isLogoLight={true} isClearBackground isSunHidden={true} />
        <img
          className="h-full w-full object-cover object-center -z-10 absolute top-0 left-0"
          alt={tent.imageAlt}
          src={tent.imageUrl}
        />
        {/* Dark overlay for better contrast */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
      </section>

      {/* Main Content - with proper spacing */}
      <main className="text-left bg-yellow-50 w-full px-4 sm:px-6 md:px-12 lg:px-24 xl:px-32 pt-12 md:pt-16 pb-20 relative">
        <div className="max-w-5xl mx-auto">
          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold leading-tight text-gray-900 mb-6">
            {tent.displayName || formattedTitle}
          </h1>

          {/* Summary/Description */}
          <div className="text-lg text-gray-600 mb-12 max-w-2xl">{summary}</div>

          {/* Accommodation Content - Gallery, Amenities, CTAs */}
          <TentExperience tent={tent} type={tentType} summary={summary} />
        </div>
      </main>

      <Footer isHome={false} />
    </>
  );
};

interface TentExperienceProps {
  tent: Tent;
  type: TentTypeV1;
  summary: React.ReactNode;
}

const TentExperience: React.FC<TentExperienceProps> = ({ tent, type }) => {
  const { gallery } = tent;
  const price = displayPrice({ priceInINR: getPrice(type), showCurrency: true });

  return (
    <AccommodationContent
      galleryImages={gallery}
      galleryAlt={tent.imageAlt}
      description={
        <div className="space-y-4">
          <p>
            Experience comfort and tradition at Camp67 with our{' '}
            <strong>{tent.displayName}</strong> accommodation. Located in the heart of
            Magh Mela 2026, this is the perfect base for your spiritual journey.
          </p>
          <p>
            Each accommodation comes with daily housekeeping, comfortable bedding, and
            access to all camp facilities including our café, yoga pavilion, and guided
            tours.
          </p>
        </div>
      }
      amenities={CAMP67_STANDARD_AMENITIES}
      tentType={type}
      priceDisplay={price}
      capacity={tent.people}
      highlights={getHighlightsForTentType(type)}
    />
  );
};
